import { ArrowRight } from "lucide-react";
import { Screen } from "../components/Screen";
import { Ref } from "../components/Ref";
import { Tag } from "../components/Tag";
import { Reveal } from "../components/Reveal";
import { Placeholder } from "../components/Placeholder";
import { useSnak, type Detail } from "../snak/SnakContext";
const AREAS: {
  title: string;
  summary: string;
  detail: Detail;
}[] = [{
  title: "Patient selection",
  summary: "Indication, blood involvement and disease compartments",
  detail: {
    kicker: "Explore · Patient selection",
    title: "Which patients does the evidence concern?",
    body: <div className="space-y-5" data-id="element-576" data-mp="vck6ts36wmi9">
          <div data-id="element-577" data-mp="vbiu7j6izryf">
            <Tag kind="context" label="Indication" data-id="element-578" data-mp="qesc6qb5umad" />
            <p className="mt-1.5" data-id="element-579" data-mp="qfbmplyhe8ed">
              Adults with MF or SS who have received at least one prior systemic therapy.<Ref n={4} data-id="element-580" data-mp="2x5i2rws9jv7f" />
            </p>
          </div>
          <div data-id="element-581" data-mp="2s6ygtwdo6jf2">
            <Tag kind="context" label="Staging" data-id="element-582" data-mp="2s7gl9gahk3y2" />
            <p className="mt-1.5" data-id="element-583" data-mp="1cq1lh458pi2m">
              Blood involvement is classified as B0, B1 or B2 and is part of formal staging.<Ref n={5} data-id="element-584" data-mp="1howbor5je8wr" />
            </p>
          </div>
          <div data-id="element-585" data-mp="1hpe57g0te90z">
            <Tag kind="trial" data-id="element-586" data-mp="1hmrz7ktko1kv" />
            <p className="mt-1.5" data-id="element-587" data-mp="1lq7tiai65pue">
              MAVORIC reported response separately for the skin, blood, lymph-node and visceral compartments.<Ref n={2} data-id="element-588" data-mp="1qi9bmd20qasc" />
            </p>
          </div>
          <div data-id="element-589" data-mp="1vgszwk433ynv">
            <Tag kind="evidence" data-id="element-590" data-mp="1vhc7b6w3z4nn" />
            <p className="mt-1.5" data-id="element-591" data-mp="1vgax14mx2m6z">
              109 patients with MF or SS treated at 18 German centers.<Ref n={1} data-id="element-592" data-mp="20e2mls039w80" />{" "}
              <Placeholder data-id="element-593" data-mp="20hgs3941uljo">MINT baseline characteristics; subgroup results only if reported in final materials</Placeholder>
            </p>
          </div>
        </div>
  }
}, {
  title: "Clinical outcomes",
  summary: "What MINT observed, and how to read it",
  detail: {
    kicker: "Explore · Clinical outcomes",
    title: "Observed outcomes in routine care",
    body: <div data-id="element-594" data-mp="f445pei8vqud">
          <Tag kind="evidence" data-id="element-595" data-mp="1icyjk8kksxbd" />
          <dl className="mt-3 divide-y divide-rule border-y border-rule" data-id="element-596" data-mp="1ie9quid8cn78">
            {[["Best overall response rate", "62.4%"], ["Median progression-free survival", "20.2 months"], ["Median time to next treatment", "14.9 months"]].map(([k, v]) => <div key={k} className="flex items-baseline justify-between gap-4 py-2.5" data-id="element-597" data-mp="1naqbh2l80jfk">
                <dt className="text-[13px]" data-id="element-598" data-mp="21arothio9jbx">{k}</dt>
                <dd className="tabular text-[16px] font-light text-ink" data-id="element-599" data-mp="1wczjnlt0o0ps">{v}</dd>
              </div>)}
          </dl>
          <p className="mt-2 text-[12px] text-muted" data-id="element-600" data-mp="2b25hb5c7hivk">
            Final MINT results, October 2026; provisional.<Ref n={1} data-id="element-601" data-mp="262j6f2juoepx" />
          </p>
          <div className="mt-5 border-l border-charcoal pl-4" data-id="element-602" data-mp="263ks00yhsjlw">
            <Tag kind="interpretation" data-id="element-603" data-mp="2k1shxox42knk" />
            <p className="mt-1.5" data-id="element-604" data-mp="2k0hhnz2vjv17">
              These single-arm, observational results add real-world context from German practice. They do not establish
              causality and cannot be compared with MAVORIC or with other therapies.
            </p>
          </div>
        </div>
  }
}, {
  title: "Treatment management",
  summary: "Posology, monitoring and safety — per prescribing information",
  detail: {
    kicker: "Explore · Treatment management",
    title: "Managing treatment in practice",
    body: <div className="space-y-4" data-id="element-605" data-mp="2fy9jro6fmzl5">
          <p data-id="element-606" data-mp="2fx6wdq8bjf9z">
            Posology, administration, premedication, monitoring, special warnings and the management of adverse reactions
            are set out in the current locally applicable SmPC / Fachinformation.<Ref n={4} data-id="element-607" data-mp="262ey0k6wwoe5" />
          </p>
          <p data-id="element-608" data-mp="2azf42v3rhr59">
            <Placeholder data-id="element-609" data-mp="2az5cj332jbxg">country-specific mandatory safety information for Germany, Austria and Switzerland</Placeholder>
          </p>
          <p data-id="element-610" data-mp="1x0fftok5ve9m">
            <Placeholder data-id="element-611" data-mp="1x0p3h5hqh9c5">MINT safety findings, only as reported in the final study materials</Placeholder>
          </p>
          <p className="text-[12px] text-muted" data-id="element-612" data-mp="220tfojblzhwk">
            No safety data are summarised in this prototype pending verified sources.
          </p>
        </div>
  }
}];
export function P7Consequence() {
  const {
    openDetail
  } = useSnak();
  return <Screen overline="The clinical consequence" refTarget={4} footnote={<p data-id="element-614" data-mp="1g64e5nwamr0m">
          The evidence concerns adults with MF or SS after at least one prior systemic therapy<Ref n={4} data-id="element-615" data-mp="1b8m5lym6udt5" />, including
          patients whose disease involves the blood.<Ref n={[2, 5]} data-id="element-616" data-mp="168gkl1afbpk1" /> MINT is observational and non-randomised; no
          superiority or causality is implied.<Ref n={1} data-id="element-617" data-mp="11aohdb4bwh0j" />
        </p>} data-id="element-613" data-mp="1k6y74volew0d">
      <Reveal delay={0.1} data-id="element-618" data-mp="11bgg2ut82uv1">
        <h1 className="mt-5 font-display text-[32px] font-medium leading-[1.1] tracking-[-0.01em]" data-id="element-619" data-mp="x8jrmnh044j6">
          What does the evolving evidence add to your{" "}
          <em className="font-medium text-[var(--s-accent)]" data-id="element-620" data-mp="2vlk2te0m3qkq">next treatment decision?</em>
        </h1>
      </Reveal>
      <Reveal delay={0.25} data-id="element-621" data-mp="2rk86yqr5a943">
        <p className="overline mt-8 text-[10px] text-[var(--s-muted)]" data-id="element-622" data-mp="dethq8p4auom">Explore further</p>
        <ul className="mt-2 border-t border-[var(--s-rule)]" data-id="element-623" data-mp="id4rykjbr77d">
          {AREAS.map(a => <li key={a.title} className="border-b border-[var(--s-rule)]" data-id="element-624" data-mp="n4d5nsroteqc">
              <button onClick={() => openDetail(a.detail)} className="group flex w-full items-center justify-between gap-4 py-4 text-left" data-id="element-625" data-mp="n6h6u2y7bzmx">
                <span className="min-w-0" data-id="element-626" data-mp="3qmwns2gnj47f">
                  <span className="block font-display text-[23px] leading-tight" data-id="element-627" data-mp="3vjepetgoqmfj">{a.title}</span>
                  <span className="mt-1 block text-[13px] text-[var(--s-muted)]" data-id="element-628" data-mp="3fm6ps5uvc4o">{a.summary}</span>
                </span>
                <ArrowRight className="h-5 w-5 shrink-0 text-[var(--s-accent)] transition-transform duration-150 group-hover:translate-x-0.5" strokeWidth={1.5} data-id="element-629" data-mp="8g9v062p9ev7" />
              </button>
            </li>)}
        </ul>
      </Reveal>

      <Reveal delay={0.6} data-id="element-630" data-mp="3fwqsm391kpuk">
        <div className="mt-8" data-id="element-631" data-mp="3fvxlh1667ybw">
          <Tag kind="implication" data-id="element-632" data-mp="3bvx7tiqwbr5w" />
          <p className="mt-3 font-display text-[26px] italic leading-snug text-[var(--s-accent)]" data-id="element-633" data-mp="3bwoqxu3b3wfa">
            Evidence informs the decision; it does not make it.
          </p>
          <p className="mt-3 text-[14px] leading-relaxed text-[var(--s-muted)]" data-id="element-634" data-mp="3kwv2vbjcwocc">
            Treatment choice rests on individual patient factors — disease stage and compartments, prior therapies,
            comorbidities and patient preference — together with safety considerations and clinical judgement.
          </p>
        </div>
      </Reveal>
    </Screen>;
}